import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "./styles.module.scss";

// Components
import LoadingPage from "views/LoadingPage";
import MessageBox from "reusableComponents/MessageBox";
import FormDropdown from "reusableComponents/FormDropdown";
import ValidatedInput from "reusableComponents/InputWithIcon";
import { FormButton } from "reusableComponents";

// Services
import GoogleCalendarService from "services/googleCalendarService";
import { fetchAllProviderSpaces } from "services/dashboard.services";

const CalendarGoogleSyncView = ({ initialSpaces = [] }) => {
  const { t } = useTranslation("global");
  const token = useSelector((state) => state.auth.token);

  // State management
  const [loading, setLoading] = useState({
    page: false,
    sync: false,
    desync: false,
  });
  const [syncMessages, setSyncMessages] = useState({
    success: null,
    errors: [],
    warnings: [],
  });
  const [desyncMessages, setDesyncMessages] = useState({
    success: null,
    errors: [],
    warnings: [],
  });
  const [formData, setFormData] = useState({
    calendar_id: "",
    place_id: "",
  });
  const [formErrors, setFormErrors] = useState({
    calendar_id: "",
    place_id: "",
  });
  const [spaces, setSpaces] = useState(initialSpaces);

  // Derived state
  const spacesToSync = spaces.filter((space) => !space.calendar_id);
  const spacesSynced = spaces.filter((space) => space.calendar_id);
  const spaceOptions = spacesToSync.map((space) => ({
    value: space.id,
    label: space.name,
  }));

  // Effects
  useEffect(() => {
    if (initialSpaces.length === 0) {
      loadSpaces();
    }
  }, []);

  // API Functions
  const loadSpaces = async () => {
    try {
      setLoading((prev) => ({ ...prev, page: true }));
      const response = await fetchAllProviderSpaces(token);
      setSpaces(response.data || response);
    } catch (error) {
      handleApiError(error, "failedToLoadSpaces");
    } finally {
      setLoading((prev) => ({ ...prev, page: false }));
    }
  };

  const handleSync = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading((prev) => ({ ...prev, sync: true }));
    resetSyncMessages();

    try {
      const response = await GoogleCalendarService.calendarSync(
        token,
        formData
      );

      if (response.success === false) {
        setSyncMessages({
          success: null,
          errors: response.errors || [t("errors.general")],
          warnings: [],
        });
        return;
      }

      setSyncMessages({
        success: response.message || t("calendar.sync.successMessage"),
        errors: [],
        warnings: [],
      });

      await loadSpaces();
      setFormData({ calendar_id: "", place_id: "" });
    } catch (error) {
      handleApiError(error, "calendarSyncFailed", "sync");
    } finally {
      setLoading((prev) => ({ ...prev, sync: false }));
    }
  };

  const handleDesync = async (calendarId) => {
    if (!calendarId) return;

    setLoading((prev) => ({ ...prev, desync: true }));
    resetDesyncMessages();

    try {
      const response = await GoogleCalendarService.calendarDesync(
        token,
        calendarId
      );

      if (response.success === false) {
        setDesyncMessages({
          success: null,
          errors: response.errors || [t("errors.general")],
          warnings: [],
        });
        return;
      }

      setDesyncMessages({
        success: response.message || t("calendar.sync.desyncSuccess"),
        errors: [],
        warnings: [],
      });

      await loadSpaces();
    } catch (error) {
      handleApiError(error, "calendarDesyncFailed", "desync");
    } finally {
      setLoading((prev) => ({ ...prev, desync: false }));
    }
  };

  // Helper Functions
  const validateForm = () => {
    const errors = {
      calendar_id: !formData.calendar_id.trim() ? t("validation.required") : "",
      place_id: !formData.place_id ? t("validation.required") : "",
    };

    setFormErrors(errors);
    resetSyncMessages();
    return !Object.values(errors).some(Boolean);
  };

  const handleApiError = (error, defaultErrorKey, context = "sync") => {
    const apiErrors = error.data?.errors || [];
    const defaultError = {
      message: t(`errors.${defaultErrorKey}`),
    };

    if (context === "sync") {
      setSyncMessages({
        success: null,
        errors: apiErrors.length > 0 ? apiErrors : [defaultError],
        warnings: error.data?.warnings || [],
      });
    } else {
      setDesyncMessages({
        success: null,
        errors: apiErrors.length > 0 ? apiErrors : [defaultError],
        warnings: error.data?.warnings || [],
      });
    }
  };

  const resetSyncMessages = () => {
    setSyncMessages({
      success: null,
      errors: [],
      warnings: [],
    });
  };

  const resetDesyncMessages = () => {
    setDesyncMessages({
      success: null,
      errors: [],
      warnings: [],
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectSpace = (value) => {
    setFormData((prev) => ({ ...prev, place_id: value }));
    setFormErrors((prev) => ({ ...prev, place_id: "" }));
  };

  if (loading.page && spaces.length === 0) {
    return <LoadingPage />;
  }

  return (
    <section className={styles.calendarMainContent}>
      <header className={styles.header}>
        <h1 className={styles.calendarPageTitle}>
          {t("calendar.sync.googleTitle")}
        </h1>
        <div className={styles.downloadManual}>
          <div
            dangerouslySetInnerHTML={{ __html: t("calendar.sync.googleText") }}
          />
        </div>
      </header>

      {/* Sync Form */}
      {spacesToSync.length > 0 && (
        <section className={styles.syncSection}>
          <h2 className={styles.sectionTitle}>{t("calendar.sync.newSync")}</h2>

          {/* Sync Messages */}

          <form className={styles.form} onSubmit={handleSync}>
            <div className={styles.formGroup}>
              <ValidatedInput
                labelAbove
                type="text"
                name="calendar_id"
                value={formData.calendar_id}
                handleChange={handleInputChange}
                label={t("calendar.sync.calendarID")}
                required
                // error={formErrors.calendar_id}
              />

              <FormDropdown
                labelAbove
                label={t("calendar.sync.space")}
                options={spaceOptions}
                selected={formData.place_id}
                onSelect={handleSelectSpace}
                placeholder={t("--")}
                required
                // error={formErrors.place_id}
              />
            </div>

            {/* Form Validation Error */}
            {Object.values(formErrors).some(Boolean) && (
              <MessageBox
                type="error"
                message={t("validation.fillAllFields")}
              />
            )}
            <MessageDisplay messages={syncMessages} type="sync" t={t} />

            <FormButton
              type="submit"
              label={t("buttons.confirm")}
              disabled={loading.sync}
              loading={loading.sync}
              variant="primary"
            />
          </form>
        </section>
      )}

      {/* Synced Calendars List */}

      {spacesSynced.length > 0 && (
        <section className={styles.syncedListSection}>
          <h2 className={styles.sectionTitle}>{t("calendar.sync.syncList")}</h2>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("calendar.sync.space")}</th>
                  <th>{t("calendar.sync.calendarID")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {spacesSynced.map((space) => (
                  <tr key={space.calendar_id}>
                    <td>{space.name}</td>
                    <td className={styles.monospace}>{space.calendar_id}</td>
                    <td>
                      <FormButton
                        size="small"
                        variant="gray"
                        label={t("calendar.sync.desync")}
                        type="button"
                        className={styles.desyncButton}
                        onClick={() => handleDesync(space.calendar_id)}
                        disabled={loading.desync}
                        loading={loading.desync}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Desync Messages */}
          {/* <MessageDisplay messages={desyncMessages} type="desync" t={t} /> */}
        </section>
      )}
    </section>
  );
};

// Helper component for displaying messages
const MessageDisplay = ({ messages, type, t }) => {
  if (!messages.success && messages.errors.length === 0) return null;

  return (
    <div className={styles.messageContainer}>
      {messages.success && (
        <MessageBox type="success" message={messages.success} />
      )}
      {messages.errors.map((error, index) => (
        <div key={`${type}-error-${index}`} className={styles.errorItem}>
          <MessageBox type="error" message={error.message} />
          {error.code === "PERMISSION_ISSUE" && (
            <div className={styles.errorSolution}>
              <p>{t("calendar.sync.fixPermissions")}</p>
              <ol>
                <li>{t("calendar.sync.step1")}</li>
                <li>{t("calendar.sync.step2")}</li>
                <li>{t("calendar.sync.step3")}</li>
              </ol>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CalendarGoogleSyncView;
